import {
  API_URL,
  v,
  put,
  debug,
  request,
  takeEvery,
  createDoRequestAction,
  assertTotalSchema,
  SagaIterator,
  call,
} from "../../lib";
import {
  CurrentMfaProof,
  freshAuthenticationProofBody,
  freshAuthenticationProofSchemaFragment,
  toProofSafeSchemaError,
} from "../../model/auth_proof";
import { doIDENTITY_MFA_ENABLED_UPDATE, doREQUEST_COMPLETE, doREQUEST_ERROR } from "../state";
import { getToken } from "./token_fetcher";

export const REQUEST_IDENTITY_MFA_ENABLED_UPDATE = "REQUEST_IDENTITY_MFA_ENABLED_UPDATE";

const URL = "/v1/web/identity-mfa-enabled-update";

export interface IdentityMFAEnabledUpdateRequestPayload {
  enabled: boolean;
  currentPassword?: string;
  currentMfaProof?: CurrentMfaProof;
}
export const doREQUEST_IDENTITY_MFA_ENABLED_UPDATE =
  createDoRequestAction<IdentityMFAEnabledUpdateRequestPayload>(
    REQUEST_IDENTITY_MFA_ENABLED_UPDATE,
  );

export const requestIdentityMfaEnabledUpdateActionCreators = {
  doREQUEST_IDENTITY_MFA_ENABLED_UPDATE,
};

export function* requestIdentityMfaEnabledUpdate({ payload, setRequestResult }: any): SagaIterator {
  debug("saga*requestIdentityMfaEnabledUpdate");

  try {
    assertTotalSchema({
      payload,
      schema: v.object({
        requestId: v.string().uuid().exist(),
        enabled: v.boolean().exist(),
        ...freshAuthenticationProofSchemaFragment,
      }),
    });
  } catch (error) {
    // the allowlist, plus the error reduced to `isJoi` and a message. The ValidationError
    // itself carries the whole rejected payload three times over -- on `_original`, on
    // `details[].context.value` and, because `assertTotalSchema` goes through `Joi.assert`, in its
    // own `message` -- so it never reaches a REQUEST_ERROR. See `toProofSafeSchemaError`.
    return yield put(
      doREQUEST_ERROR(
        {
          fromType: REQUEST_IDENTITY_MFA_ENABLED_UPDATE,
          requestId: payload?.requestId,
          error: toProofSafeSchemaError(error),
        },
        setRequestResult,
      ),
    );
  }

  const { enabled } = payload;

  const accessToken = yield call(getToken);

  const result = yield request({
    method: "post",
    url: `${API_URL}${URL}`,
    accessToken,
    data: {
      enabled,
      ...freshAuthenticationProofBody(payload),
    },
  });

  if (!result.success || result.error) {
    // Never `debug(result.error)`: an axios error carries the request body on `config.data`, so
    // the raw error is itself a copy of the password. Only the two fields that say what
    // went wrong are logged.
    debug("saga*requestIdentityMfaEnabledUpdate failed", {
      statusCode: result.statusCode,
      type: result.type,
    });
    // no `fromPayload`, so `handle_expired_token` cannot replay this request. A replay
    // stripped of its proof would be applied as an unproved change that revokes every session.
    return yield put(
      doREQUEST_ERROR(
        {
          fromType: REQUEST_IDENTITY_MFA_ENABLED_UPDATE,
          requestId: payload.requestId,
          url: URL,
          statusCode: result.statusCode,
          type: result.type,
          // the 403's `payload.clientData.field` and `payload.errorHint` reach the form
          // through here
          payload: result.payload,
        },
        setRequestResult,
      ),
    );
  }

  yield put(doIDENTITY_MFA_ENABLED_UPDATE({ mfaEnabled: enabled }));

  yield put(
    doREQUEST_COMPLETE(
      {
        fromType: REQUEST_IDENTITY_MFA_ENABLED_UPDATE,
        requestId: payload.requestId,
        ...result,
      },
      setRequestResult,
    ),
  );
}

export function* identityMfaEnabledUpdateSaga() {
  yield takeEvery(REQUEST_IDENTITY_MFA_ENABLED_UPDATE, requestIdentityMfaEnabledUpdate);
}
